import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Organization info for the user
 */
export interface UserOrganization {
  id: string;
  name: string;
  slug: string;
  logoUrl?: string | null;
}

/**
 * User profile information
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  role?: 'admin' | 'member' | 'viewer';
  organization?: UserOrganization;
}

/**
 * Authentication state interface
 */
interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearError: () => void;
}

/**
 * Authentication store
 *
 * Manages user authentication state including:
 * - Current user information
 * - Authentication status
 * - Loading and error states
 *
 * @example
 * ```tsx
 * // In a component
 * const { user, isAuthenticated, logout } = useAuthStore();
 *
 * // Select specific state (optimized for re-renders)
 * const user = useAuthStore((state) => state.user);
 * ```
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,

        // Actions
        setUser: (user) =>
          set(
            { user, isAuthenticated: !!user, isLoading: false },
            false,
            'auth/setUser'
          ),

        setLoading: (isLoading) => set({ isLoading }, false, 'auth/setLoading'),

        setError: (error) =>
          set({ error, isLoading: false }, false, 'auth/setError'),

        logout: () =>
          set(
            { user: null, isAuthenticated: false, error: null },
            false,
            'auth/logout'
          ),

        clearError: () => set({ error: null }, false, 'auth/clearError'),
      }),
      {
        name: 'cortex-auth-storage',
        // Only persist user data, not loading/error states
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
